import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch, buildApiUrl } from "./api/client";
import AdvancedFiltersModal from "./components/AdvancedFiltersModal";
import ActionMenuButton from "./components/ActionMenuButton";
import { useAppDialog } from "./components/AppDialogProvider";
import AccountSettingSelect from "./components/AccountSettingSelect";
import CardGrid from "./components/CardGrid";
import CollectorTierBadge from "./components/CollectorTierBadge";
import getEditActionButtonStyle from "./components/editActionButtonStyle";
import { useAuth } from "./authContext";
import { useCurrentUser } from "./currentUserContext";
import CsvImportPanel from "./features/import/components/CsvImportPanel";
import { useCsvImport } from "./features/import/hooks/useCsvImport";
import CardsTable from "./features/traders/components/CardsTable";
import { DEFAULT_SORT as DEFAULT_TABLE_SORT } from "./features/traders/components/CardsTable";
import AddCardsModal from "./features/traders/components/AddCardsModal";
import EditCardModal from "./features/traders/components/EditCardModal";
import TraderModal from "./features/traders/components/TraderModal";
import UploadTextModal from "./features/traders/components/UploadTextModal";
import WantsSelectionActions from "./features/traders/components/WantsSelectionActions";
import { renderConditionOption, renderEditionOption, renderLanguageOption } from "./components/dropdownOptionRenderers";
import { formatCollectorTierLimit, getCollectorTierDefinitionForRole } from "./utils/collectorTiers";
import usePaginationScroll from "./utils/usePaginationScroll";
import { getWantsBooleanLabel, getWantsConditionLabel, getWantsLanguageLabel } from "./wantsSettingsOptions";

const CARDS_PER_PAGE = 50;
const FILTER_META_DEFAULT = {
  editions: [],
  languages: [],
  conditions: [],
  priceRange: {
    min: 0,
    max: 0
  }
};

const LANGUAGE_LABELS = {
  de: "German",
  fr: "French",
  es: "Spanish",
  it: "Italian",
  jp: "Japanese",
  cn: "Chinese",
  kr: "Korean",
  ru: "Russian",
  pt: "Portuguese"
};
const FOIL_FILTER_OPTIONS = [
  { value: "all", label: "All cards" },
  { value: "nonfoil", label: "Non-foil only" },
  { value: "foil", label: "Foil only" }
];
const ALTERED_FILTER_OPTIONS = [
  { value: "all", label: "All cards" },
  { value: "notaltered", label: "Not altered" },
  { value: "altered", label: "Altered only" }
];
const SIGNED_FILTER_OPTIONS = [
  { value: "all", label: "All cards" },
  { value: "unsigned", label: "Not signed" },
  { value: "signed", label: "Signed only" }
];

function clampNumber(value, min, max, fallback) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return fallback;
  }

  return Math.min(Math.max(numericValue, min), max);
}

function getLanguageLabel(languageCode) {
  if (!languageCode) {
    return "Unknown";
  }

  return LANGUAGE_LABELS[languageCode] || String(languageCode).toUpperCase();
}

function formatDefaultSettingsSummary(defaults) {
  return `${getWantsLanguageLabel(defaults.defaultLanguage)} · ${getWantsConditionLabel(defaults.defaultCondition)} · Foil: ${getWantsBooleanLabel(defaults.defaultFoil)} · Altered: ${getWantsBooleanLabel(defaults.defaultAltered)} · Signed: ${getWantsBooleanLabel(defaults.defaultSigned)}`;
}

function getRangePercent(value, min, max) {
  if (max <= min) {
    return 0;
  }

  return ((value - min) / (max - min)) * 100;
}

function PriceRangeSlider({ min, max, step = 0.5, valueMin, valueMax, disabled, onChange }) {
  const lowerValue = Math.min(valueMin, valueMax);
  const upperValue = Math.max(valueMin, valueMax);
  const leftPercent = getRangePercent(lowerValue, min, max);
  const rightPercent = getRangePercent(upperValue, min, max);

  return (
    <div className={`price-range-slider${disabled ? " price-range-slider--disabled" : ""}`}>
      <div className="price-range-slider__track" aria-hidden="true" />
      <div
        className="price-range-slider__range"
        aria-hidden="true"
        style={{
          left: `${leftPercent}%`,
          width: `${Math.max(rightPercent - leftPercent, 0)}%`
        }}
      />
      <input
        className="price-range-slider__input"
        type="range"
        min={min}
        max={max}
        step={step}
        value={lowerValue}
        aria-label="Minimum price"
        disabled={disabled}
        onChange={(event) => {
          const nextValue = Math.min(Number(event.target.value), upperValue);
          onChange({ min: nextValue, max: upperValue });
        }}
      />
      <input
        className="price-range-slider__input price-range-slider__input--upper"
        type="range"
        min={min}
        max={max}
        step={step}
        value={upperValue}
        aria-label="Maximum price"
        disabled={disabled}
        onChange={(event) => {
          const nextValue = Math.max(Number(event.target.value), lowerValue);
          onChange({ min: lowerValue, max: nextValue });
        }}
      />
    </div>
  );
}

function getCardStackKey(card) {
  return [
    card.card || card.name || "",
    String(Boolean(card.any_edition ?? card.anyEdition)),
    card.edition || "",
    String(Boolean(card.any_language ?? card.anyLanguage)),
    card.language || "",
    String(Boolean(card.any_condition ?? card.anyCondition)),
    card.condition || "",
    String(Boolean(card.any_foil ?? card.anyFoil)),
    String(Boolean(card.foil)),
    String(Boolean(card.any_altered ?? card.anyAltered)),
    String(Boolean(card.altered)),
    String(Boolean(card.any_signed ?? card.anySigned)),
    String(Boolean(card.signed))
  ].join("||");
}

function getCardActionIds(card) {
  const qty = Math.max(1, Number(card.qty) || 1);
  return Array.from({ length: qty }, () => card.id);
}

function buildVisibleCards(cards, expandedStackKeys, stackEnabled) {
  if (!stackEnabled) {
    return cards.map((card) => ({
      ...card,
      stackKey: getCardStackKey(card),
      actionIds: getCardActionIds(card)
    }));
  }

  const visibleCards = [];
  const groupIndexes = new Map();

  for (const card of cards) {
    const stackKey = getCardStackKey(card);
    const qty = Math.max(1, Number(card.qty) || 1);

    if (expandedStackKeys.includes(stackKey)) {
      for (let index = 0; index < qty; index += 1) {
        visibleCards.push({
          ...card,
          id: `${card.id}:split:${index}`,
          qty: 1,
          stackKey,
          sourceId: card.id,
          actionIds: [card.id]
        });
      }
      continue;
    }

    if (!groupIndexes.has(stackKey)) {
      groupIndexes.set(stackKey, visibleCards.length);
      visibleCards.push({
        ...card,
        stackKey,
        actionIds: getCardActionIds(card)
      });
      continue;
    }

    const existingIndex = groupIndexes.get(stackKey);
    const existingCard = visibleCards[existingIndex];

    visibleCards[existingIndex] = {
      ...existingCard,
      qty: (existingCard.qty || 1) + qty,
      actionIds: [...existingCard.actionIds, ...getCardActionIds(card)]
    };
  }

  return visibleCards;
}

function countSelectedActionCards(cards, selectedIds) {
  return cards
    .filter((card) => selectedIds.includes(card.id))
    .reduce((sum, card) => sum + ((card.actionIds && card.actionIds.length) || 1), 0);
}

function getWantPrice(card) {
  const preferredPrice = card.user_price ?? (card.foil ? card.price_foil : card.price_trend);
  return Number(preferredPrice);
}

function getEditRequestTargets(visibleCard, editValue, editId) {
  const allActionIds = visibleCard?.actionIds || [visibleCard?.sourceId || editId];
  const availableQty = Math.max(1, allActionIds.length || Number(visibleCard?.qty) || 1);
  const requestedQty = Math.min(availableQty, Math.max(1, Number(editValue?.qty) || availableQty));

  if (requestedQty < availableQty) {
    return allActionIds.slice(0, requestedQty).map((id) => ({ id, splitOne: true }));
  }

  if (visibleCard?.sourceId) {
    return [{ id: visibleCard.sourceId, splitOne: true }];
  }

  return [...new Set(allActionIds.map((id) => String(id)))].map((id) => ({ id, splitOne: false }));
}

function hasCardAttributeChanges(visibleCard, editValue) {
  if (!visibleCard || !editValue) {
    return false;
  }

  return (
    Boolean(editValue.anyEdition) !== Boolean(visibleCard.any_edition ?? visibleCard.anyEdition)
    || Boolean(editValue.anyLanguage) !== Boolean(visibleCard.any_language ?? visibleCard.anyLanguage)
    || Boolean(editValue.anyCondition) !== Boolean(visibleCard.any_condition ?? visibleCard.anyCondition)
    || Boolean(editValue.anyFoil) !== Boolean(visibleCard.any_foil ?? visibleCard.anyFoil)
    || Boolean(editValue.anyAltered) !== Boolean(visibleCard.any_altered ?? visibleCard.anyAltered)
    || Boolean(editValue.anySigned) !== Boolean(visibleCard.any_signed ?? visibleCard.anySigned)
    ||
    (editValue.edition ?? visibleCard.edition ?? "") !== (visibleCard.edition ?? "")
    || (editValue.language ?? visibleCard.language ?? "") !== (visibleCard.language ?? "")
    || (editValue.condition ?? visibleCard.condition ?? "") !== (visibleCard.condition ?? "")
    || Boolean(editValue.foil) !== Boolean(visibleCard.foil)
    || Boolean(editValue.altered) !== Boolean(visibleCard.altered)
    || Boolean(editValue.signed) !== Boolean(visibleCard.signed)
  );
}

function getVisibleCardQuantity(visibleCard, editId) {
  return Math.max(1, visibleCard?.actionIds?.length || Number(visibleCard?.qty) || 1);
}

function getVisibleCardActionCountMap(visibleCard, editId) {
  const ids = visibleCard?.actionIds?.length
    ? visibleCard.actionIds.map((id) => String(id))
    : [String(visibleCard?.sourceId || visibleCard?.id || editId)];

  return ids.reduce((counts, id) => {
    counts.set(id, (counts.get(id) || 0) + 1);
    return counts;
  }, new Map());
}

function createPaginationState(page = 1) {
  return {
    page,
    limit: CARDS_PER_PAGE,
    totalItems: 0,
    totalPages: 1,
    totalCards: 0
  };
}

function getVisiblePageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, "ellipsis-right", totalPages];
  }

  if (currentPage >= totalPages - 3) {
    return [1, "ellipsis-left", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "ellipsis-left", currentPage - 1, currentPage, currentPage + 1, "ellipsis-right", totalPages];
}

function PaginationControls({ currentPage, totalItems, totalPages, label, onPageChange, position = "top" }) {
  const [pageInput, setPageInput] = useState(String(currentPage));

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  if (totalPages <= 1) {
    return null;
  }

  const startItem = (currentPage - 1) * CARDS_PER_PAGE + 1;
  const endItem = Math.min(totalItems, currentPage * CARDS_PER_PAGE);
  const pageNumbers = getVisiblePageNumbers(currentPage, totalPages);
  const isBottom = position === "bottom";

  const commitPageInput = () => {
    const nextPage = Number.parseInt(pageInput, 10);

    if (!Number.isFinite(nextPage)) {
      setPageInput(String(currentPage));
      return;
    }

    const clampedPage = Math.min(Math.max(nextPage, 1), totalPages);
    setPageInput(String(clampedPage));

    if (clampedPage !== currentPage) {
      onPageChange(clampedPage);
    }
  };

  return (
    <div
      className="app-pagination"
      style={{
        margin: isBottom ? "18px 0 0" : "0 0 18px",
        paddingTop: isBottom ? "10px" : 0,
        borderTop: isBottom ? "1px solid #e2e8f0" : "none"
      }}
    >
      <span className="app-pagination__summary">
        Showing {startItem}-{endItem} of {totalItems} {label}
      </span>
      <div className="app-pagination__controls">
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Previous page"
          title="Previous page"
          disabled={currentPage === 1}
          className="app-pagination__button app-pagination__button--nav"
          style={{
            background: currentPage === 1 ? "#f8fafc" : "#ffffff",
            color: currentPage === 1 ? "#94a3b8" : "#0f172a",
            cursor: currentPage === 1 ? "not-allowed" : "pointer"
          }}
        >
          <span className="app-pagination__icon" aria-hidden="true">{"<"}</span>
        </button>
        <div className="app-pagination__page-list">
          {pageNumbers.map((pageNumber) => {
            if (typeof pageNumber !== "number") {
              return (
                <span key={pageNumber} className="app-pagination__ellipsis">
                  ...
                </span>
              );
            }

            const isActive = pageNumber === currentPage;

            return (
              <button
                key={pageNumber}
                type="button"
                onClick={() => onPageChange(pageNumber)}
                aria-current={isActive ? "page" : undefined}
                className="app-pagination__button"
                style={{
                  border: isActive ? "1px solid #0f766e" : "1px solid #cbd5e1",
                  background: isActive ? "#0f766e" : "#ffffff",
                  color: isActive ? "#ffffff" : "#0f172a",
                  cursor: "pointer"
                }}
              >
                {pageNumber}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Next page"
          title="Next page"
          disabled={currentPage === totalPages}
          className="app-pagination__button app-pagination__button--nav"
          style={{
            background: currentPage === totalPages ? "#f8fafc" : "#ffffff",
            color: currentPage === totalPages ? "#94a3b8" : "#0f172a",
            cursor: currentPage === totalPages ? "not-allowed" : "pointer"
          }}
        >
          <span className="app-pagination__icon" aria-hidden="true">{">"}</span>
        </button>
        <label className="app-pagination__jump">
          Page
          <div className="app-pagination__jump-controls">
            <div className="app-pagination__select-shell">
              <input
                type="number"
                min="1"
                max={String(totalPages)}
                step="1"
                inputMode="numeric"
                aria-label="Jump to page"
                className="app-pagination__page-input"
                value={pageInput}
                onChange={(event) => setPageInput(event.target.value)}
                onBlur={commitPageInput}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    commitPageInput();
                  }

                  if (event.key === "Escape") {
                    event.preventDefault();
                    setPageInput(String(currentPage));
                    event.currentTarget.blur();
                  }
                }}
              />
            </div>
            <button
              type="button"
              className="app-pagination__button app-pagination__button--text app-pagination__jump-button"
              onClick={commitPageInput}
            >
              Go
            </button>
          </div>
        </label>
      </div>
    </div>
  );
}

function createCollectionSummaryState() {
  return {
    rarityCount: {
      common: 0,
      uncommon: 0,
      rare: 0,
      mythic: 0
    }
  };
}

export default function MyWantsView() {
  const { user } = useAuth();
  const { selectedUserId } = useCurrentUser();
  const { showAlert, showConfirm } = useAppDialog();
  const pendingScrollRestoreRef = useRef(null);
  const [wantsSettings, setWantsSettings] = useState({ defaultLanguage: "en", defaultCondition: "NM", defaultFoil: "__any__", defaultAltered: "__any__", defaultSigned: "__any__" });
  const editMode = false;
  const [viewMode, setViewMode] = useState("images");
  const [cards, setCards] = useState([]);
  const [pagination, setPagination] = useState(() => createPaginationState());
  const [collectionSummary, setCollectionSummary] = useState(() => createCollectionSummaryState());
  const [collectionCounts, setCollectionCounts] = useState({ wanted: 0 });
  const [collectionLimits, setCollectionLimits] = useState(null);
  const [selectedCards, setSelectedCards] = useState([]);
  const [filterMeta, setFilterMeta] = useState(FILTER_META_DEFAULT);
  const [setMetadata, setSetMetadata] = useState({});
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [editionFilter, setEditionFilter] = useState("");
  const [languageFilter, setLanguageFilter] = useState("");
  const [conditionFilter, setConditionFilter] = useState("");
  const [foilFilter, setFoilFilter] = useState("all");
  const [alteredFilter, setAlteredFilter] = useState("all");
  const [signedFilter, setSignedFilter] = useState("all");
  const [priceMin, setPriceMin] = useState(null);
  const [priceMax, setPriceMax] = useState(null);
  const [error, setError] = useState(null);
  const [pendingEdits, setPendingEdits] = useState({});
  const [savingTextEditIds, setSavingTextEditIds] = useState({});
  const [expandedStackKeys, setExpandedStackKeys] = useState([]);
  const [sortConfig, setSortConfig] = useState(DEFAULT_TABLE_SORT);
  const [page, setPage] = useState(1);
  const [isAddCardsModalOpen, setIsAddCardsModalOpen] = useState(false);
  const [isImportCsvModalOpen, setIsImportCsvModalOpen] = useState(false);
  const [isUploadTextModalOpen, setIsUploadTextModalOpen] = useState(false);
  const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);
  const [activeEditCardId, setActiveEditCardId] = useState(null);
  const [hoveredEditActionId, setHoveredEditActionId] = useState(null);
  const priceBounds = filterMeta.priceRange || FILTER_META_DEFAULT.priceRange;
  const rawPriceMin = clampNumber(priceMin, priceBounds.min, priceBounds.max, priceBounds.min);
  const rawPriceMax = clampNumber(priceMax, priceBounds.min, priceBounds.max, priceBounds.max);
  const effectivePriceMin = Math.min(rawPriceMin, rawPriceMax);
  const effectivePriceMax = Math.max(rawPriceMin, rawPriceMax);
  const hasPriceFilter = priceMin !== null || priceMax !== null;

  const resetTransientViewState = () => {
    setExpandedStackKeys([]);
    setSelectedCards([]);
    setActiveEditCardId(null);
  };

  const loadCards = useCallback(async (nextPage = page) => {
    try {
      const params = new URLSearchParams({
        page: String(nextPage),
        limit: String(CARDS_PER_PAGE),
        includeWanted: "true"
      });

      if (searchTerm) params.set("search", searchTerm);
      if (editionFilter) params.set("edition", editionFilter);
      if (languageFilter) params.set("language", languageFilter);
      if (conditionFilter) params.set("condition", conditionFilter);
      if (foilFilter !== "all") params.set("foil", foilFilter);
      if (alteredFilter !== "all") params.set("altered", alteredFilter);
      if (signedFilter !== "all") params.set("signed", signedFilter);
      if (priceMin !== null) params.set("minPrice", String(effectivePriceMin));
      if (priceMax !== null) params.set("maxPrice", String(effectivePriceMax));
      if (sortConfig.key) {
        params.set("sortBy", sortConfig.key);
        params.set("sortDirection", sortConfig.direction);
      }

      const response = await apiFetch(`/api/wants/${selectedUserId}/collection?${params.toString()}`, { userId: selectedUserId });
      const data = await response.json().catch(() => ({}));
      const nextCards = Array.isArray(data?.cards) ? data.cards : [];
      const nextPagination = data?.pagination || {
        ...createPaginationState(nextPage),
        totalItems: nextCards.length,
        totalPages: Math.max(1, Math.ceil(nextCards.length / CARDS_PER_PAGE)),
        totalCards: nextCards.reduce((sum, card) => sum + (card.qty || 1), 0)
      };
      const nextCounts = data?.counts || { wanted: 0 };

      setCards(nextCards.map((card) => ({
        ...card,
        visibilityGroup: card.visibility_group || card.visibilityGroup || "wanted",
        anyEdition: Boolean(card.any_edition ?? card.anyEdition),
        anyLanguage: Boolean(card.any_language ?? card.anyLanguage),
        anyCondition: Boolean(card.any_condition ?? card.anyCondition),
        anyFoil: Boolean(card.any_foil ?? card.anyFoil),
        anyAltered: Boolean(card.any_altered ?? card.anyAltered),
        anySigned: Boolean(card.any_signed ?? card.anySigned)
      })));
      setFilterMeta(data?.filterMeta || FILTER_META_DEFAULT);
      setPagination(nextPagination);
      setCollectionSummary({
        rarityCount: {
          common: Number(data?.summary?.rarityCount?.common) || 0,
          uncommon: Number(data?.summary?.rarityCount?.uncommon) || 0,
          rare: Number(data?.summary?.rarityCount?.rare) || 0,
          mythic: Number(data?.summary?.rarityCount?.mythic) || 0
        }
      });
      setCollectionCounts({
        wanted: Number(nextCounts.wanted) || 0
      });
      setCollectionLimits(data?.collectionLimits || null);
      if (nextPagination.page !== nextPage) {
        setPage(nextPagination.page);
      }
      setError(null);
    } catch (err) {
      console.error("Wants load error:", err);
      setError("Backend is not running. Make sure PostgreSQL and server on port 3000 are started.");
      setCards([]);
      setFilterMeta(FILTER_META_DEFAULT);
      setPagination(createPaginationState(nextPage));
      setCollectionSummary(createCollectionSummaryState());
      setCollectionCounts({ wanted: 0 });
      setCollectionLimits(null);
    }
  }, [alteredFilter, conditionFilter, editionFilter, effectivePriceMax, effectivePriceMin, foilFilter, languageFilter, page, priceMax, priceMin, searchTerm, selectedUserId, signedFilter, sortConfig.direction, sortConfig.key]);

  useEffect(() => {
    loadCards(page);
  }, [loadCards, page]);

  const handleTableSortChange = useCallback((nextSortConfig) => {
    setPage(1);
    setSortConfig(nextSortConfig);
  }, []);

  useEffect(() => {
    const setCodes = [...new Set(filterMeta.editions.map((edition) => String(edition.code || "").trim().toLowerCase()).filter(Boolean))];
    const missingSetCodes = setCodes.filter((setCode) => !setMetadata[setCode]);

    if (missingSetCodes.length === 0) {
      return undefined;
    }

    let isCancelled = false;

    apiFetch(`/api/traders/search/sets?codes=${encodeURIComponent(missingSetCodes.join(","))}`, { userId: selectedUserId })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not fetch set metadata");
        }

        if (isCancelled) {
          return;
        }

        setSetMetadata((current) => ({
          ...current,
          ...(data.sets || {})
        }));
      })
      .catch((error) => {
        console.error("Set metadata error:", error);
      });

    return () => {
      isCancelled = true;
    };
  }, [filterMeta.editions, selectedUserId, setMetadata]);

  useEffect(() => {
    let isDisposed = false;

    async function loadWantsSettings() {
      try {
        const response = await apiFetch(`/api/account/wants-settings/${selectedUserId}`, { userId: selectedUserId });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not load My Wants settings");
        }

        if (!isDisposed) {
          setWantsSettings({
            defaultLanguage: String(data?.settings?.defaultLanguage || "en").toLowerCase(),
            defaultCondition: String(data?.settings?.defaultCondition || "NM").toUpperCase(),
            defaultFoil: String(data?.settings?.defaultFoil || "__any__").toLowerCase(),
            defaultAltered: String(data?.settings?.defaultAltered || "__any__").toLowerCase(),
            defaultSigned: String(data?.settings?.defaultSigned || "__any__").toLowerCase()
          });
        }
      } catch (err) {
        if (!isDisposed) {
          console.error("My Wants settings load error:", err);
          setWantsSettings({ defaultLanguage: "en", defaultCondition: "NM", defaultFoil: "__any__", defaultAltered: "__any__", defaultSigned: "__any__" });
        }
      }
    }

    loadWantsSettings();

    return () => {
      isDisposed = true;
    };
  }, [selectedUserId]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      const nextSearchTerm = searchInput.trim();

      if (nextSearchTerm === searchTerm) {
        return;
      }

      resetTransientViewState();
      setPage(1);
      setSearchTerm(nextSearchTerm);
    }, 180);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchInput, searchTerm]);

  const refreshCards = () => {
    setExpandedStackKeys([]);
    return loadCards(page);
  };

  const clearPendingEdit = (editId) => {
    setPendingEdits((prev) => {
      if (!prev[editId]) {
        return prev;
      }

      const next = { ...prev };
      delete next[editId];
      return next;
    });
  };

  const setTextEditSaving = (editId, isSaving) => {
    setSavingTextEditIds((prev) => {
      if (isSaving) {
        return {
          ...prev,
          [editId]: true
        };
      }

      if (!prev[editId]) {
        return prev;
      }

      const next = { ...prev };
      delete next[editId];
      return next;
    });
  };

  const {
    file,
    setFile,
    fileInputRef,
    importStatus,
    handleUpload,
    resetImportState,
    totalImportRows,
    isImportActive,
    isImportPreparing,
    showDeterminateImportProgress,
    importProgressPercent,
    importStatusLabel,
  } = useCsvImport({
    uploadUrl: buildApiUrl("/api/wants/upload"),
    getStatusUrl: (importId) => buildApiUrl(`/api/wants/upload/${importId}/status`),
    getRequestOptions: () => ({}),
    onCompleted: () => {
      refreshCards();
    }
  });

  const handleCloseImportCsvModal = useCallback(() => {
    setIsImportCsvModalOpen(false);

    if (!isImportActive) {
      resetImportState();
    }
  }, [isImportActive, resetImportState]);

  useEffect(() => {
    if (importStatus?.stage === "completed") {
      handleCloseImportCsvModal();
    }
  }, [handleCloseImportCsvModal, importStatus?.stage]);

  const handleOpenEditModal = (card) => {
    setActiveEditCardId(card.id);
  };

  const renderEditActionButton = ({ card }) => {
    const actionId = String(card.id);
    const isHighlighted = hoveredEditActionId === actionId;

    return (
      <button
        type="button"
        aria-label={`Edit ${card.card || "card"}`}
        title="Edit card"
        onMouseEnter={() => {
          setHoveredEditActionId(actionId);
        }}
        onMouseLeave={() => {
          setHoveredEditActionId((currentId) => (currentId === actionId ? null : currentId));
        }}
        onClick={(event) => {
          event.stopPropagation();
          handleOpenEditModal(card);
        }}
        style={getEditActionButtonStyle(isHighlighted)}
      >
      <svg width="13" height="13" viewBox="0 0 18 18" fill="none" aria-hidden="true">
        <path d="M12.9 2.6a1.4 1.4 0 0 1 2 2L7.1 12.4 4 13l.6-3.1 8.3-7.3Z" fill="currentColor" />
        <path d="M10.9 4.3l2.8 2.8" stroke="#ffffff" strokeWidth="1" strokeLinecap="round" />
        <path d="M3.8 14.2h10.4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
      </button>
    );
  };

  const toggleSelect = (cardId) => {
    setSelectedCards((prev) => prev.includes(cardId)
      ? prev.filter((id) => id !== cardId)
      : [...prev, cardId]);
  };

  const handleSplitSelected = () => {
    const selectedStackKeys = stackedVisibleCards
      .filter((card) => selectedCards.includes(card.id) && (card.qty || 1) > 1)
      .map((card) => card.stackKey);

    if (selectedStackKeys.length === 0) {
      return;
    }

    setExpandedStackKeys((prev) => [...new Set([...prev, ...selectedStackKeys])]);
    setSelectedCards([]);
  };

  const handleRemoveSelected = async () => {
    const selectedWantCards = stackedVisibleCards.filter((card) => selectedCards.includes(card.id));
    const selectedWantCount = countSelectedActionCards(stackedVisibleCards, selectedCards);

    if (selectedWantCards.length === 0) {
      return;
    }

    const shouldRemoveSelected = await showConfirm({
      title: "Remove Wanted Cards",
      message: `Remove ${selectedWantCount} card${selectedWantCount === 1 ? "" : "s"} from wants?`,
      confirmLabel: "Remove cards",
      tone: "danger"
    });

    if (!shouldRemoveSelected) {
      return;
    }

    try {
      for (const card of selectedWantCards) {
        const requestTargets = card.sourceId
          ? [{ id: card.sourceId, splitOne: true }]
          : (card.actionIds || [card.id]).map((id) => ({ id, splitOne: true }));

        for (const target of requestTargets) {
          const response = await apiFetch(`/api/wants/delete/${target.id}`, {
            userId: selectedUserId,
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ splitOne: target.splitOne })
          });

          if (!response.ok) {
            const json = await response.json().catch(() => ({}));
            throw new Error(json.error || "Could not remove selected wanted card");
          }
        }
      }

      setExpandedStackKeys([]);
      setSelectedCards([]);
      refreshCards();
    } catch (err) {
      console.error("Remove wanted cards error:", err);
      await showAlert({
        title: "Remove Wanted Cards",
        message: err.message || "Could not remove selected wanted cards",
        tone: "danger"
      });
    }
  };

  const handleRemoveAllWanted = async () => {
    if (wantedCount === 0) {
      return;
    }

    const shouldRemoveAllWanted = await showConfirm({
      title: "Clear My Wants",
      message: `Remove all ${wantedCount} wanted card${wantedCount === 1 ? "" : "s"} from My Wants?`,
      confirmLabel: "Remove all",
      tone: "danger"
    });

    if (!shouldRemoveAllWanted) {
      return;
    }

    try {
      const response = await apiFetch("/api/wants/reset", {
        userId: selectedUserId,
        method: "DELETE"
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Could not remove all wanted cards");
      }

      await showAlert({
        title: "My Wants Updated",
        message: `${wantedCount} wanted card${wantedCount === 1 ? "" : "s"} removed.`
      });
      resetTransientViewState();
      refreshCards();
    } catch (err) {
      console.error("Remove all wanted cards error:", err);
      await showAlert({
        title: "Clear My Wants",
        message: err.message || "Could not remove all wanted cards",
        tone: "danger"
      });
    }
  };

  const stackedVisibleCards = buildVisibleCards(cards, expandedStackKeys, true);
  const visibleTotalCost = stackedVisibleCards.reduce((sum, card) => {
    const unitPrice = getWantPrice(card);
    const quantity = Math.max(1, Number(card.qty) || 1);

    if (!Number.isFinite(unitPrice)) {
      return sum;
    }

    return sum + (unitPrice * quantity);
  }, 0);
  const wantedCount = collectionCounts.wanted;
  const visibleCardsById = stackedVisibleCards.reduce((map, card) => {
    map[card.id] = card;
    return map;
  }, {});
  const activeEditCard = activeEditCardId ? visibleCardsById[activeEditCardId] || null : null;

  const updateVisibleCardQuantity = async (editId, nextQty) => {
    const visibleCard = visibleCardsById[editId];
    const currentQty = getVisibleCardQuantity(visibleCard, editId);
    const targetQty = Math.max(1, Number(nextQty) || 1);

    if (targetQty === currentQty) {
      return;
    }

    if (targetQty > currentQty) {
      const anchorId = String(visibleCard?.sourceId || visibleCard?.actionIds?.[0] || visibleCard?.id || editId);
      const response = await apiFetch(`/api/wants/update-quantity/${anchorId}`, {
        userId: selectedUserId,
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ delta: targetQty - currentQty })
      });
      const json = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(json.error || "Could not update wanted card quantity");
      }

      return;
    }

    let remainingToRemove = currentQty - targetQty;
    const actionCountMap = getVisibleCardActionCountMap(visibleCard, editId);

    for (const [id, count] of actionCountMap.entries()) {
      if (remainingToRemove <= 0) {
        break;
      }

      const delta = -Math.min(count, remainingToRemove);
      const response = await apiFetch(`/api/wants/update-quantity/${id}`, {
        userId: selectedUserId,
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ delta })
      });
      const json = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(json.error || "Could not update wanted card quantity");
      }

      remainingToRemove -= Math.abs(delta);
    }
  };

  const saveCardEdit = async (editId, editValue) => {
    const visibleCard = visibleCardsById[editId];
    const requestedQty = Math.max(1, Number(editValue?.qty) || 1);
    const currentQty = getVisibleCardQuantity(visibleCard, editId);

    if (!hasCardAttributeChanges(visibleCard, editValue) && requestedQty !== currentQty) {
      await updateVisibleCardQuantity(editId, requestedQty);
      return;
    }

    const requestTargets = getEditRequestTargets(visibleCard, editValue, editId);
    const { edition, language, condition, foil, altered, signed, scryfallId } = editValue;
    const {
      anyEdition = false,
      anyLanguage = false,
      anyCondition = false,
      anyFoil = false,
      anyAltered = false,
      anySigned = false
    } = editValue;

    for (const target of requestTargets) {
      const res = await apiFetch(`/api/wants/update/${target.id}`, {
        userId: selectedUserId,
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          edition,
          language,
          condition,
          foil,
          altered,
          signed,
          anyEdition,
          anyLanguage,
          anyCondition,
          anyFoil,
          anyAltered,
          anySigned,
          scryfallId,
          splitOne: target.splitOne
        })
      });
      const json = await res.json().catch(() => ({}));

      if (!res.ok || json.success === false) {
        throw new Error(json.error || "Update failed");
      }
    }
  };

  const autoSaveTextCardEdit = async (editId, editValue) => {
    setTextEditSaving(editId, true);

    try {
      await saveCardEdit(editId, editValue);
      clearPendingEdit(editId);
      await loadCards(page);
    } catch (err) {
      console.error("Auto-save wanted card edit error:", err);
      throw err;
    } finally {
      setTextEditSaving(editId, false);
    }
  };

  const hasVisibleCards = stackedVisibleCards.length > 0;
  const activeFilterCount = [
    Boolean(searchTerm),
    Boolean(editionFilter),
    Boolean(languageFilter),
    Boolean(conditionFilter),
    foilFilter !== "all",
    alteredFilter !== "all",
    signedFilter !== "all",
    hasPriceFilter
  ].filter(Boolean).length;
  const activeAdvancedFilterCount = [
    Boolean(editionFilter),
    Boolean(languageFilter),
    Boolean(conditionFilter),
    foilFilter !== "all",
    alteredFilter !== "all",
    signedFilter !== "all"
  ].filter(Boolean).length;
  const resultsSummaryLabel = pagination.totalItems === 1 ? "matching stack" : "matching stacks";
  const hasSelectedStacks = stackedVisibleCards.some((card) => selectedCards.includes(card.id) && (card.qty || 1) > 1);
  const hasSelectedCards = stackedVisibleCards.some((card) => selectedCards.includes(card.id));
  const selectedCount = countSelectedActionCards(stackedVisibleCards, selectedCards);
  const showTextView = viewMode === "text";
  const resultsSectionRef = usePaginationScroll(pagination.page);
  const handlePageChange = async (nextPage) => {
    if (nextPage === page) {
      return;
    }

    if (Object.keys(pendingEdits).length > 0) {
      const shouldDiscardChanges = await showConfirm({
        title: "Discard Unsaved Changes",
        message: "Changing page will discard unsaved edits. Continue?",
        confirmLabel: "Discard changes",
        tone: "danger"
      });

      if (!shouldDiscardChanges) {
        return;
      }

      setPendingEdits({});
    }

    setExpandedStackKeys([]);
    setSelectedCards([]);
    setActiveEditCardId(null);
    setPage(nextPage);
  };

  useLayoutEffect(() => {
    if (pendingScrollRestoreRef.current === null) {
      return undefined;
    }

    const targetScrollTop = pendingScrollRestoreRef.current;
    const scrollingElement = document.scrollingElement || document.documentElement || document.body;

    const restoreScrollPosition = () => {
      if (scrollingElement) {
        scrollingElement.scrollTop = targetScrollTop;
      }

      window.scrollTo({ top: targetScrollTop, left: 0, behavior: "auto" });
    };

    restoreScrollPosition();

    const animationFrameOne = window.requestAnimationFrame(() => {
      restoreScrollPosition();

      window.requestAnimationFrame(() => {
        restoreScrollPosition();
      });
    });

    const timeoutId = window.setTimeout(() => {
      restoreScrollPosition();
      pendingScrollRestoreRef.current = null;
    }, 120);

    return () => {
      window.cancelAnimationFrame(animationFrameOne);
      window.clearTimeout(timeoutId);
    };
  }, [viewMode]);

  const handleViewModeChange = (nextViewMode) => {
    if (nextViewMode === viewMode) {
      return;
    }

    const scrollingElement = document.scrollingElement || document.documentElement || document.body;
    const scrollPosition = scrollingElement ? scrollingElement.scrollTop : window.scrollY;

    pendingScrollRestoreRef.current = scrollPosition;
    setViewMode(nextViewMode);
  };

  const updatePriceRangeFilter = (nextMin, nextMax, options = {}) => {
    const boundedMin = clampNumber(nextMin, priceBounds.min, priceBounds.max, priceBounds.min);
    const boundedMax = clampNumber(nextMax, priceBounds.min, priceBounds.max, priceBounds.max);
    const resolvedMin = Math.min(boundedMin, boundedMax);
    const resolvedMax = Math.max(boundedMin, boundedMax);
    const { preserveExplicitBounds = false } = options;

    resetTransientViewState();
    setPage(1);
    setPriceMin(!preserveExplicitBounds && Math.abs(resolvedMin - priceBounds.min) < 0.001 ? null : resolvedMin);
    setPriceMax(!preserveExplicitBounds && Math.abs(resolvedMax - priceBounds.max) < 0.001 ? null : resolvedMax);
  };

  const handlePriceRangeChange = ({ min: nextMin, max: nextMax }) => {
    updatePriceRangeFilter(nextMin, nextMax, { preserveExplicitBounds: true });
  };

  const handlePriceInputChange = (bound, rawValue) => {
    if (rawValue === "") {
      if (bound === "min") {
        updatePriceRangeFilter(priceBounds.min, effectivePriceMax);
        return;
      }

      updatePriceRangeFilter(effectivePriceMin, priceBounds.max);
      return;
    }

    const parsedValue = Number(rawValue);

    if (!Number.isFinite(parsedValue)) {
      return;
    }

    if (bound === "min") {
      updatePriceRangeFilter(parsedValue, effectivePriceMax);
      return;
    }

    updatePriceRangeFilter(effectivePriceMin, parsedValue);
  };

  const applyFilterValue = (setter, value) => {
    resetTransientViewState();
    setPage(1);
    setter(value);
  };

  const clearAdvancedFilters = () => {
    resetTransientViewState();
    setPage(1);
    setEditionFilter("");
    setLanguageFilter("");
    setConditionFilter("");
    setFoilFilter("all");
    setAlteredFilter("all");
    setSignedFilter("all");
  };

  const canAdjustPriceRange = priceBounds.max > priceBounds.min;
  const currentUserRole = String(user?.role || "collector").trim().toLowerCase();
  const currentWantTier = getCollectorTierDefinitionForRole(collectionLimits?.tier ?? user?.collectorTier, currentUserRole);
  const wantUsage = Number(collectionLimits?.usage?.myWants || 0);
  const wantLimit = currentUserRole === "admin"
    ? currentWantTier.myWantsLimit
    : collectionLimits?.limits?.myWants ?? currentWantTier.myWantsLimit;
  const summaryTotalValue = visibleTotalCost;
  const summaryTotalCards = Number(pagination.totalCards) || 0;
  const summaryRarityCount = collectionSummary.rarityCount || { common: 0, uncommon: 0, rare: 0, mythic: 0 };
  const addCardsAndSummaryGridStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: "16px",
    alignItems: "stretch",
    marginBottom: "20px"
  };
  const topCardStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "14px 16px",
    border: "1px solid #dbe4f0",
    borderRadius: "16px",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)",
    minWidth: 0,
    width: "min(400px, 100%)",
    boxSizing: "border-box"
  };
  const topSummaryCardStyle = {
    ...topCardStyle,
    minWidth: "min(500px, 100%)",
    width: "min(500px, 100%)",
    gap: "14px"
  };

  return (
    <div className="app-responsive-shell collection-page collection-page--wants">
      <div className="account-page-header">
        <h1 style={{ margin: "0 0 16px" }}>My Wants</h1>
        <p className="account-copy">
          Add cards to your wanted list and edit specific details for each card.
        </p>
      </div>
      {error && (
        <div
          style={{
            background: "#ffebee",
            border: "2px solid #c62828",
            color: "#c62828",
            padding: "15px",
            borderRadius: "8px",
            marginBottom: "20px",
            fontWeight: "bold"
          }}
        >
          ⚠️ {error}
        </div>
      )}

      <div style={addCardsAndSummaryGridStyle}>
        <div style={topCardStyle}>
          <div>
            <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#64748b", fontWeight: 700 }}>
              Manage Cards
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap", marginTop: "8px" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#0f172a", fontWeight: 700 }}>
                <CollectorTierBadge tier={currentWantTier.id} role={currentUserRole} size="md" />
                {currentWantTier.label}
              </span>
              <span style={{ fontSize: "12px", color: "#475569", fontWeight: 600 }}>
                {new Intl.NumberFormat().format(wantUsage)} / {formatCollectorTierLimit(wantLimit)} cards in My Wants
              </span>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "2px", fontSize: "12px", lineHeight: 1.5, color: "#64748b", maxWidth: "720px" }}>
            <Link
              to="/account/my-wants-settings"
              style={{ color: "#0f766e", fontWeight: 700, textDecoration: "none", width: "fit-content" }}
            >
              My Wants Settings:
            </Link>
            <span>{formatDefaultSettingsSummary(wantsSettings)}</span>
          </div>
          <div className="collection-top-actions" style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
            <ActionMenuButton
              label="Add Cards"
              buttonVariant="primary"
              items={[
                {
                  label: "Add 1 Card",
                  onClick: () => setIsAddCardsModalOpen(true)
                },
                {
                  label: "Import CSV",
                  onClick: () => setIsImportCsvModalOpen(true)
                },
                {
                  label: "Upload Text",
                  onClick: () => setIsUploadTextModalOpen(true)
                }
              ]}
            />
            <ActionMenuButton
              label="Bulk actions"
              items={[
                {
                  label: `Remove all (${wantedCount})`,
                  onClick: handleRemoveAllWanted,
                  disabled: wantedCount === 0,
                  tone: "danger"
                }
              ]}
            />
          </div>
        </div>

        <div style={topSummaryCardStyle}>
          <div>
            <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#64748b", fontWeight: 700 }}>
              Summary
            </div>
            <div style={{ fontSize: "14px", color: "#334155", marginTop: "8px" }}>
              Visible My Wants totals for the current filters.
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "18px", alignItems: "baseline", color: "#334155", fontSize: "14px" }}>
              <span>
                Cost: <strong style={{ color: "#15803d" }}>{summaryTotalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</strong>
                <span
                  title="Total value is calculated from the filtered wants using Cardmarket reference prices."
                  aria-label="Total value is calculated from the filtered wants using Cardmarket reference prices."
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "16px",
                    height: "16px",
                    marginLeft: "6px",
                    transform: "translateY(-1px)",
                    borderRadius: "999px",
                    background: "#facc15",
                    color: "#713f12",
                    fontSize: "11px",
                    fontWeight: 800,
                    lineHeight: 1,
                    cursor: "help"
                  }}
                >
                  !
                </span>
              </span>
              <span>
                Cards: <strong>{summaryTotalCards}</strong>
              </span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "18px", alignItems: "baseline", color: "#334155", fontSize: "14px" }}>
              <span>Commons: <strong>{summaryRarityCount.common}</strong></span>
              <span>Uncommons: <strong>{summaryRarityCount.uncommon}</strong></span>
              <span>Rares: <strong>{summaryRarityCount.rare}</strong></span>
              <span>Mythics: <strong>{summaryRarityCount.mythic}</strong></span>
            </div>
          </div>
          <div style={{ fontSize: "12px", color: hasVisibleCards ? "#475569" : "#94a3b8", lineHeight: 1.5 }}>
            {hasVisibleCards
              ? `${pagination.totalItems} ${resultsSummaryLabel} match the current view.`
              : "No visible cards match the current filters right now."}
          </div>
        </div>
      </div>

      <AddCardsModal
        isOpen={isAddCardsModalOpen}
        onClose={() => setIsAddCardsModalOpen(false)}
        onAdded={() => {
          refreshCards();
          setSelectedCards([]);
        }}
        userId={selectedUserId}
        title="Add Wants"
        searchApiBasePath="/api/traders"
        submitUrl="/api/wants/add"
        defaultLanguage={wantsSettings.defaultLanguage}
        defaultCondition={wantsSettings.defaultCondition}
        defaultFoil={wantsSettings.defaultFoil}
        defaultAltered={wantsSettings.defaultAltered}
        defaultSigned={wantsSettings.defaultSigned}
        showLanguageField={true}
        enableWantWildcards={true}
      />

      <TraderModal
        isOpen={isImportCsvModalOpen}
        onClose={handleCloseImportCsvModal}
        title="Import Wants CSV"
        width="760px"
      >
        <CsvImportPanel
          title="Upload your wanted cards"
          file={file}
          fileInputRef={fileInputRef}
          isImportActive={isImportActive}
          importStatus={importStatus}
          isImportPreparing={isImportPreparing}
          showDeterminateImportProgress={showDeterminateImportProgress}
          importProgressPercent={importProgressPercent}
          importStatusLabel={importStatusLabel}
          totalImportRows={totalImportRows}
          onFileChange={setFile}
          onUpload={handleUpload}
        />
      </TraderModal>

      <UploadTextModal
        isOpen={isUploadTextModalOpen}
        onClose={() => setIsUploadTextModalOpen(false)}
        userId={selectedUserId}
        submitUrl="/api/wants/upload-text"
        title="Upload Wants Text"
        defaultLanguage={wantsSettings.defaultLanguage}
        defaultCondition={wantsSettings.defaultCondition}
        onUploaded={() => {
          refreshCards();
          setSelectedCards([]);
        }}
      />

      <AdvancedFiltersModal
        isOpen={isAdvancedFiltersOpen}
        onClose={() => setIsAdvancedFiltersOpen(false)}
        title="Want filters"
        activeCount={activeAdvancedFilterCount}
        onClear={clearAdvancedFilters}
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px" }}>
          <label style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px", color: "#334155", fontWeight: 600 }}>
            Edition
            <AccountSettingSelect
              title="Edition"
              value={editionFilter}
              options={[
                { value: "", label: "All editions" },
                ...filterMeta.editions.map((edition) => ({
                  value: edition.code,
                  label: `${setMetadata[String(edition.code || "").trim().toLowerCase()]?.name || edition.label}${edition.code ? ` (${String(edition.code).toUpperCase()})` : ""}`,
                  setCode: String(edition.code || "").trim().toLowerCase(),
                  setMetadata: setMetadata[String(edition.code || "").trim().toLowerCase()],
                  showSetSymbol: Boolean(edition.code)
                }))
              ]}
              onChange={(nextValue) => applyFilterValue(setEditionFilter, nextValue)}
              renderSelectedValue={(nextValue, option) => renderEditionOption(nextValue, option, "All editions")}
              renderOption={(nextValue, option) => renderEditionOption(nextValue, option)}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px", color: "#334155", fontWeight: 600 }}>
            Language
            <AccountSettingSelect
              title="Language"
              value={languageFilter}
              options={[
                { value: "", label: "All languages" },
                ...filterMeta.languages.map((language) => ({ value: language, label: getLanguageLabel(language) }))
              ]}
              onChange={(nextValue) => applyFilterValue(setLanguageFilter, nextValue)}
              renderSelectedValue={(nextValue) => nextValue ? renderLanguageOption(nextValue) : <span className="account-setting-select-preview-label">All languages</span>}
              renderOption={(nextValue) => nextValue ? renderLanguageOption(nextValue) : <span className="account-setting-select-preview-label">All languages</span>}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px", color: "#334155", fontWeight: 600 }}>
            Condition
            <AccountSettingSelect
              title="Condition"
              value={conditionFilter}
              options={[
                { value: "", label: "All conditions" },
                ...filterMeta.conditions.map((condition) => ({ value: condition, label: condition }))
              ]}
              onChange={(nextValue) => applyFilterValue(setConditionFilter, nextValue)}
              renderSelectedValue={(nextValue) => nextValue ? renderConditionOption(nextValue) : <span className="account-setting-select-preview-label">All conditions</span>}
              renderOption={(nextValue) => nextValue ? renderConditionOption(nextValue) : <span className="account-setting-select-preview-label">All conditions</span>}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px", color: "#334155", fontWeight: 600 }}>
            Foil
            <AccountSettingSelect
              title="Foil"
              value={foilFilter}
              options={FOIL_FILTER_OPTIONS}
              onChange={(nextValue) => applyFilterValue(setFoilFilter, nextValue)}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px", color: "#334155", fontWeight: 600 }}>
            Altered
            <AccountSettingSelect
              title="Altered"
              value={alteredFilter}
              options={ALTERED_FILTER_OPTIONS}
              onChange={(nextValue) => applyFilterValue(setAlteredFilter, nextValue)}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px", color: "#334155", fontWeight: 600 }}>
            Signed
            <AccountSettingSelect
              title="Signed"
              value={signedFilter}
              options={SIGNED_FILTER_OPTIONS}
              onChange={(nextValue) => applyFilterValue(setSignedFilter, nextValue)}
            />
          </label>
        </div>
        <div className="collection-price-panel" style={{ display: "flex", flexDirection: "column", gap: "10px", fontSize: "13px", color: "#334155", fontWeight: 600, padding: 0, minWidth: 0, marginTop: "16px" }}>
          <div className="collection-price-header" style={{ display: "flex", gap: "12px", alignItems: "baseline", flexWrap: "wrap" }}>
            <span>Price range</span>
          </div>
          <div className="collection-price-row" style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "flex-end" }}>
            <label className="collection-price-field" style={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: "12px", color: "#64748b", fontWeight: 600, flex: "0 1 84px", minWidth: 0 }}>
              Min
              <input
                type="number"
                min={priceBounds.min}
                max={effectivePriceMax}
                step="0.5"
                value={priceMin ?? ""}
                placeholder={String(priceBounds.min)}
                disabled={!canAdjustPriceRange}
                onChange={(event) => handlePriceInputChange("min", event.target.value)}
                className="collection-input"
                style={{
                  height: "40px",
                  borderRadius: "12px",
                  border: "1px solid #cbd5e1",
                  padding: "0 8px",
                  background: "#ffffff",
                  color: "#0f172a"
                }}
              />
            </label>
            <div className="collection-price-slider" style={{ flex: "1 1 120px", width: "auto", minWidth: "110px" }}>
              <PriceRangeSlider
                min={priceBounds.min}
                max={priceBounds.max}
                step={0.5}
                valueMin={effectivePriceMin}
                valueMax={effectivePriceMax}
                disabled={!canAdjustPriceRange}
                onChange={handlePriceRangeChange}
              />
            </div>
            <label className="collection-price-field" style={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: "12px", color: "#64748b", fontWeight: 600, flex: "0 1 84px", minWidth: 0 }}>
              Max
              <input
                type="number"
                min={effectivePriceMin}
                max={priceBounds.max}
                step="0.5"
                value={priceMax ?? ""}
                placeholder={String(priceBounds.max)}
                disabled={!canAdjustPriceRange}
                onChange={(event) => handlePriceInputChange("max", event.target.value)}
                className="collection-input"
                style={{
                  height: "40px",
                  borderRadius: "12px",
                  border: "1px solid #cbd5e1",
                  padding: "0 8px",
                  background: "#ffffff",
                  color: "#0f172a"
                }}
              />
            </label>
          </div>
        </div>
      </AdvancedFiltersModal>

      <div className="collection-mode-bar" style={{ display: "none" }}>
        <span style={{ fontWeight: 600 }}>View:</span>
        <div
          role="tablist"
          aria-label="View mode"
          style={{
            position: "relative",
            display: "inline-grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            padding: "4px",
            minWidth: "118px",
            borderRadius: "999px",
            background: "#e2e8f0",
            boxShadow: "inset 0 1px 2px rgba(15, 23, 42, 0.12)"
          }}
        >
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              top: "4px",
              bottom: "4px",
              left: viewMode === "images" ? "4px" : "calc(50% + 2px)",
              width: "calc(50% - 6px)",
              borderRadius: "999px",
              background: "#ffffff",
              boxShadow: "0 4px 14px rgba(15, 23, 42, 0.14)",
              transition: "left 0.2s ease"
            }}
          />
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "images"}
            aria-label="View as images"
            title="View as images"
            onClick={() => handleViewModeChange("images")}
            style={{
              position: "relative",
              zIndex: 1,
              border: "none",
              background: "transparent",
              padding: "10px 16px",
              borderRadius: "999px",
              color: viewMode === "images" ? "#0f172a" : "#64748b",
              fontWeight: 600,
              fontSize: "14px",
              cursor: "pointer",
              transition: "color 0.2s ease",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <rect x="2" y="2" width="5" height="5" rx="1.2" fill="currentColor" />
              <rect x="11" y="2" width="5" height="5" rx="1.2" fill="currentColor" />
              <rect x="2" y="11" width="5" height="5" rx="1.2" fill="currentColor" />
              <rect x="11" y="11" width="5" height="5" rx="1.2" fill="currentColor" />
            </svg>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "text"}
            aria-label="View as text"
            title="View as text"
            onClick={() => handleViewModeChange("text")}
            style={{
              position: "relative",
              zIndex: 1,
              border: "none",
              background: "transparent",
              padding: "10px 16px",
              borderRadius: "999px",
              color: viewMode === "text" ? "#0f172a" : "#64748b",
              fontWeight: 600,
              fontSize: "14px",
              cursor: "pointer",
              transition: "color 0.2s ease",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <circle cx="3.5" cy="4" r="1.25" fill="currentColor" />
              <circle cx="3.5" cy="9" r="1.25" fill="currentColor" />
              <circle cx="3.5" cy="14" r="1.25" fill="currentColor" />
              <rect x="6" y="3" width="9" height="2" rx="1" fill="currentColor" />
              <rect x="6" y="8" width="9" height="2" rx="1" fill="currentColor" />
              <rect x="6" y="13" width="9" height="2" rx="1" fill="currentColor" />
            </svg>
          </button>
        </div>
      </div>

      <div className="collection-top-grid">
      <div
        className="collection-filter-panel collection-top-card"
        style={{
          marginBottom: "16px",
          width: "min(916px, 100%)",
          padding: "14px",
          borderRadius: "16px",
          border: "1px solid #dbe4f0",
          background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
          boxShadow: "0 8px 18px rgba(15, 23, 42, 0.05)"
        }}
      >
        <div className="collection-filter-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap", marginBottom: "14px" }}>
          <div>
            <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "#64748b", fontWeight: 700 }}>
              Filter cards
            </div>
            <div style={{ fontSize: "14px", color: "#334155", marginTop: "4px" }}>
              {pagination.totalItems} {resultsSummaryLabel} · {pagination.totalCards} cards total
            </div>
          </div>
        </div>
        <div className="app-responsive-filters collection-filters" style={{ display: "grid", gap: "14px" }}>
          <div style={{ display: "flex", alignItems: "flex-end", gap: "12px", flexWrap: "wrap" }}>
            <label className="collection-field collection-field--wide" style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px", color: "#334155", fontWeight: 600, flex: "1 1 320px", width: "min(420px, 100%)", minWidth: "280px" }}>
              Search
              <input
                type="search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Card name, edition or collector #"
                className="collection-input"
                style={{
                  height: "42px",
                  width: "100%",
                  borderRadius: "12px",
                  border: "1px solid #cbd5e1",
                  padding: "0 14px",
                  boxSizing: "border-box",
                  background: "#ffffff",
                  color: "#0f172a"
                }}
              />
            </label>
            <button
              type="button"
              onClick={() => setIsAdvancedFiltersOpen(true)}
              className="collection-button"
              style={{
                borderRadius: "12px",
                border: activeAdvancedFilterCount > 0 ? "1px solid #0f766e" : "1px solid #cbd5e1",
                background: activeAdvancedFilterCount > 0 ? "#ccfbf1" : "#ffffff",
                color: activeAdvancedFilterCount > 0 ? "#115e59" : "#0f172a",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                height: "42px",
                flex: "0 0 auto"
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M2 3.5h12l-4.6 5.2v3.2l-2.8 1.6V8.7L2 3.5Z" fill="currentColor" />
              </svg>
              {activeAdvancedFilterCount > 0 ? `Filters (${activeAdvancedFilterCount})` : "Filters"}
            </button>
          </div>
        </div>
      </div>
      </div>

      <div className="browse-users-view-toolbar" style={{ justifyContent: "flex-start", gap: "1.5rem", marginTop: -4, marginBottom: 16 }}>
        <div
          className="browse-users-view-switch"
          role="tablist"
          aria-label="View mode"
        >
          <div
            aria-hidden="true"
            className="browse-users-view-switch-indicator"
            style={{ left: viewMode === "images" ? "4px" : "calc(50% + 2px)" }}
          />
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "images"}
            aria-label="View as images"
            title="View as images"
            onClick={() => handleViewModeChange("images")}
            className="browse-users-view-switch-button"
            style={{ color: viewMode === "images" ? "#0f172a" : "#64748b" }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <rect x="2" y="2" width="5" height="5" rx="1.2" fill="currentColor" />
              <rect x="11" y="2" width="5" height="5" rx="1.2" fill="currentColor" />
              <rect x="2" y="11" width="5" height="5" rx="1.2" fill="currentColor" />
              <rect x="11" y="11" width="5" height="5" rx="1.2" fill="currentColor" />
            </svg>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "text"}
            aria-label="View as text"
            title="View as text"
            onClick={() => handleViewModeChange("text")}
            className="browse-users-view-switch-button"
            style={{ color: viewMode === "text" ? "#0f172a" : "#64748b" }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <circle cx="3.5" cy="4" r="1.25" fill="currentColor" />
              <circle cx="3.5" cy="9" r="1.25" fill="currentColor" />
              <circle cx="3.5" cy="14" r="1.25" fill="currentColor" />
              <rect x="6" y="3" width="9" height="2" rx="1" fill="currentColor" />
              <rect x="6" y="8" width="9" height="2" rx="1" fill="currentColor" />
              <rect x="6" y="13" width="9" height="2" rx="1" fill="currentColor" />
            </svg>
          </button>
        </div>
      </div>

      {(() => {
        if (!hasVisibleCards) {
          return <p>{activeFilterCount > 0 ? "No wants match the current filters." : "No wanted cards selected."}</p>;
        }

        return (
          <div ref={resultsSectionRef}>
            <PaginationControls
              currentPage={pagination.page}
              totalItems={pagination.totalItems}
              totalPages={pagination.totalPages}
              label="wants"
              onPageChange={handlePageChange}
            />
            {showTextView ? (
              <div className="app-responsive-table">
                <CardsTable
                  cards={stackedVisibleCards}
                  selectedCards={selectedCards}
                  toggleSelect={toggleSelect}
                  showSelectionIndicatorColumn={true}
                  badgeType="WANT"
                  userId={selectedUserId}
                  editMode={editMode}
                  pendingEdits={pendingEdits}
                  setPendingEdits={setPendingEdits}
                  savingEditIds={savingTextEditIds}
                  onAutoSaveEdit={autoSaveTextCardEdit}
                  searchApiBasePath="/api/traders"
                  priceColumnLabel="Price"
                  emptyPriceCellLabel="-"
                  sortConfig={sortConfig}
                  onSortChange={handleTableSortChange}
                  renderRowAction={({ card }) => renderEditActionButton({ card })}
                />
              </div>
            ) : (
              <CardGrid
                cards={stackedVisibleCards}
                selectedCards={selectedCards}
                toggleSelect={toggleSelect}
                badgeType="WANT"
                preferNormalImage={true}
                pendingEdits={pendingEdits}
                setPendingEdits={setPendingEdits}
                renderCardAction={({ card }) => renderEditActionButton({ card })}
              />
            )}
            <PaginationControls
              currentPage={pagination.page}
              totalItems={pagination.totalItems}
              totalPages={pagination.totalPages}
              label="wants"
              onPageChange={handlePageChange}
              position="bottom"
            />
            <EditCardModal
              isOpen={Boolean(activeEditCard)}
              card={activeEditCard}
              pendingEdit={activeEditCard ? pendingEdits[activeEditCard.id] || {} : {}}
              onClose={() => setActiveEditCardId(null)}
              userId={selectedUserId}
              onApply={async (nextEdit) => {
                if (!activeEditCard) {
                  return;
                }

                await saveCardEdit(activeEditCard.id, nextEdit);
                setPendingEdits((prev) => {
                  const next = { ...prev };
                  delete next[activeEditCard.id];
                  return next;
                });
                refreshCards();
                setActiveEditCardId(null);
              }}
              title="Edit Wanted Card"
              searchApiBasePath="/api/traders"
              enableWantWildcards={true}
            />
          </div>
        );
      })()}

      <WantsSelectionActions
        selectedCards={selectedCards}
        hasSelectedCards={hasSelectedCards}
        hasSelectedStacks={hasSelectedStacks}
        selectedCount={selectedCount}
        onSplit={handleSplitSelected}
        onRemove={handleRemoveSelected}
      />

    </div>
  );
}
